import { useSanitiseTimelineHtml } from 'hooks/timelines/use_sanitise_timeline_html'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { cn } from 'util/cn'

import { useIsAppRoute } from '../../../hooks/routes/use_is_app_route'
import { makeGetChat } from '../../../reduxes/slices/api/selectors'
import { getSelectedTimelineId } from '../../../reduxes/slices/selected/selectors'
import { makeGetTimeline } from '../../../reduxes/slices/timelines/selectors'
import { EditTimelineForm } from '../actions/edit_timeline_form'
import Timeline from '../timeline'
import { ReplyChatReference } from './reply_chat_reference'

export default function Chat({
  timeline,
  withActionsBar,
  withTopicSubject,
  withLink,
  withTimelineInfo,
  avatarType = 'default',
  threadedFrom,
  className,
  isAReplyTimeline,
  showReply,
  withTimelineWrapper,
  ...props
}) {
  const getChat = useMemo(makeGetChat, [])
  const chat = useSelector((state) => getChat(state, timeline?.tl_id))
  const getTimeline = useMemo(makeGetTimeline, [])
  const replyTimeline = useSelector((state) =>
    getTimeline(state, timeline?.reply_timeline_id),
  )
  const selectedTimelineId = useSelector(getSelectedTimelineId)
  const isSelected = selectedTimelineId === timeline?.id
  const navigate = useNavigate()

  // Handle internal links (ie: ones that can be navigated to in the app)
  const isAppRoute = useIsAppRoute()
  const timelineHtmlRef = useRef(null)

  useEffect(() => {
    const timelineHtml = timelineHtmlRef.current
    if (!timelineHtml) {
      return
    }

    // Delegate the click event on our timeline to intercept anchors
    // If we can navToUrl (ie: it's an internal link), prevent default behaviour (see electron/main.js)
    // Otherwise, use the default behaviour
    const onAnchorClicked = (e) => {
      if (e.target.tagName.toUpperCase() === 'A') {
        const { href } = e.target
        if (isAppRoute(href)) {
          e.preventDefault()
          const { pathname } = new URL(href)
          navigate(pathname)
        }
      }
    }

    timelineHtml.addEventListener('click', onAnchorClicked)

    return () => {
      timelineHtml.removeEventListener('click', onAnchorClicked)
    }
  }, [navigate, isAppRoute])

  const showEditor = isSelected && !isAReplyTimeline
  const handle = chat?.handle

  const chatHtml = useSanitiseTimelineHtml(chat)

  if (!timeline || !chat) return null

  const isDeleted = !!chat?.deleted_at

  const showReplyImp = showReply && replyTimeline && !isAReplyTimeline

  return (
    <Timeline
      timeline={timeline}
      item={chat}
      handle={handle}
      align={'left'}
      className={cn(className, threadedFrom && 'threaded-timeline')}
      withActionsBar={withActionsBar && !isDeleted}
      withEmojiReactions={withActionsBar && !isDeleted}
      withTopicSubject={withTopicSubject}
      withTimelineInfo={withTimelineInfo}
      withLink={withLink}
      threadedFrom={threadedFrom}
      avatarType={isAReplyTimeline ? 'none' : avatarType}
      {...props}
    >
      <div className={'chat align-left'}>
        {showEditor ? (
          <EditTimelineForm timeline={timeline} item={chat} />
        ) : (
          <>
            {showReplyImp && <ReplyChatReference timeline={replyTimeline} />}
            <div
              ref={timelineHtmlRef}
              dangerouslySetInnerHTML={{ __html: chatHtml }}
            />
          </>
        )}
      </div>
    </Timeline>
  )
}

Chat.propTypes = {
  timeline: PropTypes.object.isRequired,
  withActionsBar: PropTypes.bool,
  withTopicSubject: PropTypes.bool,
  withLink: PropTypes.bool,
  threadedFrom: PropTypes.bool,
  isAReplyTimeline: PropTypes.bool,
  showReply: PropTypes.bool,
  withTimelineWrapper: PropTypes.bool,
  avatarType: PropTypes.oneOf(['default', 'spacer', 'none', 'popup']),
}

Chat.defaultProps = {
  withActionsBar: true,
  withTopicSubject: false,
  withLink: false,
  threadedFrom: false,
  withTimelineWrapper: true,
}
